/**
 * Altyazı görünüm tercihleri — tarayıcıda saklanır.
 *
 * ## Neden sunucuda değil?
 *
 * Bunlar kişisel görüntüleme tercihleri ( Plex'te de öyle): hesaplar arası
 * taşınması gerekmiyor, sunucuya yazmak her ayarlama değişikliğinde yeni bir
 * uç ve migrasyon demek. `localStorage` yeterli ve anında etkir.
 *
 * ## Neden `useState` içinde okuyoruz?
 *
 * `localStorage` render sırasında okunursa ilk boyama ile ilk etkileşim
 * arasında değer değişebilir (altyazı iki kez zıplar). Tembel başlatıcı
 * `useState(() => ...)` bunu önler: değer yalnızca ilk render'da bir kez okunur.
 */

import type { CSSProperties } from 'react';
import { useCallback, useMemo, useState } from 'react';
import {
  clampPositionPercent,
  computeSubtitleBottomPx,
  DEFAULT_POSITION_PERCENT,
  EMPTY_PICTURE_BOUNDS,
  letterboxPositionPercent,
  type PictureBounds,
  SIZE_SCALES,
  type SubtitleSize,
  subtitleVars,
} from './subtitleStyle.ts';
import { recordSwallowed } from './swallowed.ts';

/** Metin renkleri. `Accent` Plex'teki sarı, `Bright` saf beyaz. */
export const SUBTITLE_COLORS = {
  white: { label: 'Beyaz', value: '#ffffff' },
  yellow: { label: 'Sarı', value: '#f5d547' },
  cyan: { label: 'Camgöbeği', value: '#7fe3ff' },
} as const;

export type SubtitleColor = keyof typeof SUBTITLE_COLORS;

/** Arka plan: gölge (varsayılan) / kutu / hiç yok. */
export const SUBTITLE_BACKGROUNDS = {
  shadow: 'Gölge',
  box: 'Kutu',
  none: 'Yok',
} as const;

export type SubtitleBackground = keyof typeof SUBTITLE_BACKGROUNDS;

export interface SubtitlePrefs {
  size: SubtitleSize;
  color: SubtitleColor;
  background: SubtitleBackground;
  bold: boolean;
  /**
   * Dikey konum kaydırması (yüzde; kutu yüksekliğine göre).
   *
   * `0` = resmin alt kenarı, negatif = siyah banta doğru, pozitif =
   * görüntünün içine yukarı. Bkz. `computeSubtitleBottomPx`.
   */
  position: number;
}

export const DEFAULT_PREFS: SubtitlePrefs = {
  // Gerçek sunucuda altyazı önce "baya küçük", sonra (ölçüm hatası yüzünden)
  // "kocaman" göründü. Ölçüm düzeltildi: artık **normal** seviye doğru
  // değerdir — 5% oranı Plex/Netflix ölçeğidir. `large` kullanıcı isterse.
  size: 'medium',
  color: 'white',
  background: 'shadow',
  bold: true,
  position: DEFAULT_POSITION_PERCENT,
};

/**
 * Saklama anahtarı **sürümlüdür**.
 *
 * v1, ölçüm hatası döneminde yazıldı: kullanıcı `large` seçmişti ama bu
 * tercih, altyazının 10 kat büyük çıktığı bozuk ölçek modeline göre
 * kaydedilmiş bir *duygu* değeri. v2 ölçüm modelini (video yüksekliğine göre
 * %5) esas alır, eski değer bilerek yok sayılır — kullanıcı yeni varsayılana
 * döner. Anahtarı geri almak, ölçeği değiştirdiğimizde eski oranları
 * sürdürmekten kaçınmaktır.
 *
 * `position` alanı sonradan eklendi ama **yeni sürüm açılmadı**: eksik alan
 * `sanitizePrefs` tarafından varsayılanla doldurulur, yani eski kayıtlar
 * bozulmadan yeni ayarı da alır. Yeni sürüm yalnızca eski değerlerin **yanlış**
 * olduğu durumda gerekir.
 */
const STORAGE_KEY = 'pbfork.subtitlePrefs.v2';

function readStored(): Partial<SubtitlePrefs> {
  if (typeof localStorage === 'undefined') return {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return {};
    return parsed as Partial<SubtitlePrefs>;
  } catch (error) {
    recordSwallowed('useSubtitlePrefs', error);
    // Bozuk/eskimiş veri tercihi sıfırlamak için yeterli.
    return {};
  }
}

/** Saklanan değerler geçerli mi? Geçersizse varsayılana düşer. */
export function sanitizePrefs(input: Partial<SubtitlePrefs>): SubtitlePrefs {
  const size = input.size;
  const color = input.color;
  const background = input.background;
  return {
    size: size !== undefined && size in SIZE_SCALES ? size : DEFAULT_PREFS.size,
    color: color !== undefined && color in SUBTITLE_COLORS ? color : DEFAULT_PREFS.color,
    background:
      background !== undefined && background in SUBTITLE_BACKGROUNDS
        ? background
        : DEFAULT_PREFS.background,
    bold: typeof input.bold === 'boolean' ? input.bold : DEFAULT_PREFS.bold,
    position:
      typeof input.position === 'number'
        ? clampPositionPercent(input.position)
        : DEFAULT_PREFS.position,
  };
}

export interface SubtitlePrefsApi {
  prefs: SubtitlePrefs;
  /** Altyazı öğesine uygulanacak CSS değişkenleri. */
  style: CSSProperties;
  setSize: (size: SubtitleSize) => void;
  setColor: (color: SubtitleColor) => void;
  setBackground: (background: SubtitleBackground) => void;
  setBold: (bold: boolean) => void;
  setPosition: (percent: number) => void;
  /**
   * Altyazıyı **alttaki siyah bandın ortasına** indirir.
   *
   * @returns `false` → siyah bant yok (16:9 kaynak); konum değiştirilmedi.
   *   Böyle bir durumda düğme yanıltıcı olurdu.
   */
  fitToLowerBar: () => boolean;
  /** Bu içerikte siyah bant var mı? (düğmeyi gizlemek için) */
  hasLetterbox: boolean;
  reset: () => void;
}

export interface SubtitlePrefsGeometry {
  /** Kutu (`.watch__stage`) yüksekliği. */
  stageHeightPx: number | null;
  /** Resmin siyah bant dahil gerçek sınırları. */
  bounds: PictureBounds;
  /** Kontrol çubuğu yüksekliği + güvenlik payı. */
  controlsSafePx: number | null;
}

export function useSubtitlePrefs(
  stageHeightPx: number | null,
  geometry?: SubtitlePrefsGeometry,
): SubtitlePrefsApi {
  const bounds = geometry?.bounds ?? EMPTY_PICTURE_BOUNDS;
  const controlsSafePx = geometry?.controlsSafePx ?? null;

  const [prefs, setPrefs] = useState<SubtitlePrefs>(() => ({
    ...DEFAULT_PREFS,
    ...sanitizePrefs(readStored()),
  }));

  // Değişiklik anında sakla: kullanıcı sayfayı kapatırsa tercih kalsın.
  const update = useCallback((patch: Partial<SubtitlePrefs>) => {
    setPrefs((previous) => {
      const next = sanitizePrefs({ ...previous, ...patch });
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch (error) {
        recordSwallowed('useSubtitlePrefs', error);
        // Depolama kapalı (gizli sekme / kota) — tercih yine de bu oturumda
        // geçerli olur, sadece hatırlanmaz.
      }
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      recordSwallowed('useSubtitlePrefs', error);
      /* yok sayılır */
    }
    setPrefs(DEFAULT_PREFS);
  }, []);

  const style = useMemo(
    () =>
      ({
        ...subtitleVars(stageHeightPx, prefs.size, prefs.bold),
        '--sub-color': SUBTITLE_COLORS[prefs.color].value,
        '--sub-bg': BACKGROUND_CSS[prefs.background],
        '--sub-shadow': prefs.background === 'none' ? 'none' : undefined,
        /*
         * Konum piksel olarak buraya yazılır: `bottom: var(--sub-bottom)`.
         * Ölçüm yoksa değişken **yazılmaz** ve CSS yedeği (`max(12%, 92px)`)
         * devreye girer — yani ilk karede ölçüm gelene kadar eski davranış
         * korunur, sonra anında yeni modele geçilir.
         */
        '--sub-bottom': (() => {
          const bottom = computeSubtitleBottomPx({
            stageHeightPx,
            controlsSafePx,
            positionPercent: prefs.position,
            ...bounds,
          });
          return bottom === null ? undefined : `${bottom}px`;
        })(),
      }) as CSSProperties,
    [stageHeightPx, prefs, bounds, controlsSafePx],
  );

  const fitToLowerBar = useCallback((): boolean => {
    const target = letterboxPositionPercent(bounds);
    if (target === null) return false;
    update({ position: target });
    return true;
  }, [bounds, update]);

  return {
    prefs,
    style,
    setSize: (size) => update({ size }),
    setColor: (color) => update({ color }),
    setBackground: (background) => update({ background }),
    setBold: (bold) => update({ bold }),
    setPosition: (percent) => update({ position: percent }),
    fitToLowerBar,
    hasLetterbox: letterboxPositionPercent(bounds) !== null,
    reset,
  };
}

const BACKGROUND_CSS = {
  shadow: 'rgb(0 0 0 / 62%)',
  box: 'rgb(0 0 0 / 88%)',
  none: 'transparent',
} as const;
